<script setup lang="ts">
import { computed, defineAsyncComponent } from "vue";

import LoadingCircle from "@/shared/components/svgs/LoadingCircle.vue";

import type {
    ButtonProps,
    ButtonEmits,
} from "@/shared/components/temparary/components/base/button/BaseButtonModel";

const props = defineProps<ButtonProps>();
const emits = defineEmits<ButtonEmits>();

const iconComponent = computed(() => {
    return defineAsyncComponent(
        () => import(`@/shared/components/temparary/components/base/svgs/${props.icon}.vue`),
    );
});

const customClass = computed(() => {
    const stylePerColor = {
        "bg-primary-red text-white cursor-pointer active:bg-secondary-red active:text-primary-red":
            props.color === "primary" && !props.disabled,
        "bg-white text-primary-red border border-primary-red cursor-pointer active:bg-primary-red active:text-white":
            props.color === "primary-border" && !props.disabled,
        "bg-white text-primary-blue border border-primary-blue cursor-pointer active:bg-primary-blue active:text-white":
            props.color === "primary-border-blue" && !props.disabled,
        "bg-white text-primary-black border border-secondary-gray cursor-pointer active:bg-secondary-gray active:border-primary-black":
            props.color === "normal" && !props.disabled,
        "bg-white text-primary-blue border border-primary-blue cursor-pointer active:bg-secondary-blue active:border-primary-blue":
            props.color === "primary-blue" && !props.disabled,
        "bg-white text-primary-yellow border border-primary-yellow cursor-pointer active:bg-secondary-yellow active:border-primary-yellow":
            props.color === "primary-yellow" && !props.disabled,
    };

    const styleBase = {
        "px-[2rem] py-[1.05rem] text-base rounded-[0.8rem]": !props.isInTable,
        "px-[1.4rem] py-[0.6rem] text-base rounded-[0.8rem]": props.isInTable,
    };

    const stylePerDisabled = {
        "bg-secondary-gray text-primary-gray": props.disabled,
    };

    const styleWidth = {
        "w-full sm:w-fit": props.responsiveMobileWidth,
        "w-fit": !props.responsiveMobileWidth && !props.isWidthFull,
        "w-full": props.isWidthFull,
    };

    return {
        ...stylePerColor,
        ...styleBase,
        ...stylePerDisabled,
        ...styleWidth,
    };
});

const handleClick = () => {
    emits("click");
};
</script>

<template>
    <button
        :type="type"
        class="flex items-center justify-center gap-[0.5rem] text-center transition-all font-pretended font-medium whitespace-nowrap"
        :class="customClass"
        :disabled="props.disabled || false"
        @click.stop="handleClick"
    >
        <div v-if="icon && iconComponent" class="flex justify-center items-center">
            <component
                :is="iconComponent"
                :width="iconWidth"
                :height="iconHeight"
                :color="iconColor"
            />
        </div>
        <div v-if="!icon && loading" class="flex justify-center items-center">
            <LoadingCircle />
        </div>
        {{ value }}
    </button>
</template>

<style scoped></style>
